import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Card,
  CardBody,
  Flex,
  Grid,
  Spinner,
  useDisclosure,
} from '@chakra-ui/react';
import { inquiryApi } from '@texas/api/endpoints/inquiryApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { reactEvents } from '@bridge/reactEvents';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { Icons } from '@texas/components/shared/Icons';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { WarningSummary } from '@texas/components/shared/WarningSummary';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, DateFormat } from '@texas/utils/helpers/dateHelper';
import { Locale } from '@texas/i18n/types';
import { useFilterInquiries } from './useFilterInquiries';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { InquiryTable } from './inquiryTable/InquiryTable';
import { InquiryMetaCard } from './metadata/InquiryMetaCard';
import { NonMatrixInquiryTierPrices } from './NonMatrixInquiryTierPrices';
import { InquiryChargesReadOnly } from './InquiryChargesReadOnly';
import { InquiryChargesDrawer } from './InquiryChargesDrawer';
import {
  InquiryDetailProvider,
  useInquiryDetail,
} from './InquiryDetailContext';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { BodyOverride } from '@texas/components/shared/BodyOverride';
import { useMatrixCellFilters } from './useMatrixCellFilters';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { ReactSelectOption } from '@texas/types';
import { canAcceptInquiry, InquiryStatusDialog } from './InquiryStatusDialog';

interface Props {
  inquiryId: number;
  variantId: number;
}

export function InquiryDetailPage({ inquiryId, variantId }: Props) {
  return (
    <InquiryDetailProvider inquiryId={inquiryId}>
      <InquiryDetailPageContent inquiryId={inquiryId} variantId={variantId} />
    </InquiryDetailProvider>
  );
}

function InquiryDetailPageContent({ variantId }: Props) {
  const { t } = useTranslation();
  const warningContainerRef = useRef<HTMLDivElement>(null);
  const { inquiry, loading, error, refetch } = useInquiryDetail();
  const { request: sendRequest, loading: sending } = useApiRequest(
    inquiryApi.sendInquiry,
  );
  const {
    isOpen: isAcceptOpen,
    onOpen: onAcceptOpen,
    onClose: onAcceptClose,
  } = useDisclosure();
  const {
    isOpen: isRejectOpen,
    onOpen: onRejectOpen,
    onClose: onRejectClose,
  } = useDisclosure();
  const {
    useCurrenciesData: { findCurrency },
    currenciesData: [, refetchCurrencies],
  } = useMetadata();

  const {
    data: pageData,
    error: pageError,
    refetch: refetchPage,
  } = useApiResource(inquiryApi.getByVariant);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isCopyOpen,
    onOpen: onCopyOpen,
    onClose: onCopyClose,
  } = useDisclosure();

  useEffect(() => {
    refetchCurrencies();
  }, [refetchCurrencies]);

  useEffect(() => {
    refetchPage({ variantId });
  }, [refetchPage, variantId]);

  useEffect(() => {
    if (!inquiry) return;

    reactEvents.entityVisited.dispatch({
      id: inquiry.id,
      type: 'inquiry',
      customerId: inquiry.customerId,
      label: inquiry.articleItemCode
        ? `${inquiry.articleItemCode} / ${inquiry.formattedInquiryNumber}`
        : inquiry.formattedInquiryNumber,
      navobj: {
        ...(inquiry.articleId ? { articleId: inquiry.articleId } : {}),
        variantId,
      },
    });
  }, [inquiry, variantId]);

  const versions = useMemo(() => pageData?.versions ?? [], [pageData]);

  const controlledInquiriesAsContext = useMemo(
    () => inquiry?.controlledInquiries ?? [],
    [inquiry],
  );

  const masterSlot = versions.find(
    (v) =>
      v.compositionGroupPurchaseInfoId ===
      inquiry?.compositionGroupPurchaseInfoId,
  );
  const masterCompositionGroupId = masterSlot?.compositionGroupId ?? null;

  const matrixCellFilterSource = useMemo(
    () => [
      ...controlledInquiriesAsContext,
      ...versions.filter(
        (slot) =>
          slot.controlledById !== null &&
          slot.compositionGroupId === masterCompositionGroupId,
      ),
    ],
    [controlledInquiriesAsContext, masterCompositionGroupId, versions],
  );

  const matrixCellFilters = useMatrixCellFilters({
    items: matrixCellFilterSource,
  });

  const filteredControlledInquiries = matrixCellFilters.filterItems(
    controlledInquiriesAsContext,
  );

  const filteredVersions = matrixCellFilters.filterItems(versions);

  const {
    filteredInquiries: displayedInquiries,
    missingCellSlots: allMissingSlots,
    allTierColumns,
    targetTierQuantities,
    hasAnyX,
    hasAnyY,
  } = useFilterInquiries({
    mode: 'matrix',
    versions: filteredVersions,
    inquiries: filteredControlledInquiries,
  });

  const missingCellSlots = allMissingSlots.filter(
    (slot) => slot.compositionGroupId === masterCompositionGroupId,
  );

  if (error) return <ErrorDetails error={error} />;
  if (pageError) return <ErrorDetails error={pageError} />;
  if (!inquiry) return null;

  const isMatrixMaster = inquiry.isMatrix && inquiry.controlledById === null;
  const matrixTierColumns = Array.from(
    new Set([
      ...allTierColumns,
      ...inquiry.tierPrices.map((tier) => tier.quantity),
      ...inquiry.allTargetTiers.map((tier) => tier.quantity),
    ]),
  ).sort((a, b) => a - b);
  const matrixTargetTierQuantities = new Set([
    ...targetTierQuantities,
    ...inquiry.allTargetTiers.map((tier) => tier.quantity),
  ]);
  const currencyCode = findCurrency(inquiry.currencyId)?.code ?? '';
  const sendInquiry = async () => {
    window.open(`/api/inquiries/${inquiry.id}/email`, '_blank');
    const response = await sendRequest(inquiry.id);
    if (!response.hasError) refetch();
  };

  return (
    <BodyOverride>
      <Box
        ref={warningContainerRef}
        px={{ base: 4, md: 6 }}
        py={5}
        pb={12}
        color="inquiryOverview.text"
      >
        <Flex
          align={{ base: 'flex-start', lg: 'center' }}
          justify="space-between"
          gap={4}
          wrap="wrap"
          mb={5}
        >
          <Breadcrumb fontSize="sm" color="inquiryOverview.mutedText">
            <BreadcrumbItem>
              <BreadcrumbLink
                cursor="pointer"
                onClick={() =>
                  reactEvents.routingChanged.dispatch({
                    to: 'variant.inquiry',
                    navObj: { variantId, status: 'all' },
                  })
                }
              >
                {t('inquiry.inquiries')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink
                color="inquiryOverview.text"
                fontWeight="semibold"
              >
                {inquiry.formattedInquiryNumber || '—'}
              </BreadcrumbLink>
            </BreadcrumbItem>
            {loading && (
              <BreadcrumbItem>
                <Spinner size="xs" />
              </BreadcrumbItem>
            )}
          </Breadcrumb>
          <Flex gap={2} wrap="wrap">
            <Button
              size="sm"
              variant="entityDirectorySecondary"
              leftIcon={<Icons.ContentCopy boxSize={4} />}
              onClick={onCopyOpen}
            >
              {t('inquiry.copy')}
            </Button>
            {inquiry.archived == null && canAcceptInquiry(inquiry) && (
              <Button
                size="sm"
                variant="entityDirectorySecondary"
                onClick={onAcceptOpen}
              >
                {t('inquiry.acceptInquiry')}
              </Button>
            )}
            {inquiry.archived == null && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  borderColor="texas.burntSienna.500"
                  color="texas.burntSienna.500"
                  onClick={onRejectOpen}
                >
                  {t('inquiry.rejectInquiry')}
                </Button>
                <Button
                  size="sm"
                  variant="entityDirectoryPrimary"
                  isLoading={sending}
                  onClick={sendInquiry}
                >
                  {t('inquiry.sendInquiry')}
                </Button>
              </>
            )}
          </Flex>
        </Flex>

        {inquiry.acceptedDate != null && (
          <Alert
            status="success"
            variant="left-accent"
            borderRadius="md"
            mb={4}
          >
            <AlertIcon />
            <AlertDescription fontSize="sm">
              {t('inquiry.acceptedBanner', {
                date: formatDate(
                  Locale.En,
                  inquiry.acceptedDate,
                  DateFormat.Datetime,
                ),
                name: inquiry.acceptedByUserName ?? '—',
              })}
            </AlertDescription>
          </Alert>
        )}
        {inquiry.archived != null && (
          <Alert
            status="warning"
            variant="left-accent"
            borderRadius="md"
            mb={4}
          >
            <AlertIcon />
            <AlertDescription fontSize="sm">
              {t('inquiry.archivedBanner', {
                date: formatDate(
                  Locale.En,
                  inquiry.archived,
                  DateFormat.Datetime,
                ),
                name: inquiry.archivedByUserName ?? '—',
              })}
            </AlertDescription>
          </Alert>
        )}
        <WarningSummary
          containerRef={warningContainerRef}
          title={t('inquiry.requiredActions')}
        />
        <Grid
          templateColumns={{
            base: 'minmax(0, 1fr)',
            '2xl': 'minmax(0, 1.85fr) minmax(360px, 0.95fr)',
          }}
          gap={5}
          alignItems="start"
        >
          <Box minW={0}>
            <InquiryMetaCard data={inquiry} />
            <Card
              variant="dark"
              bg="inquiryOverview.surface"
              border="1px solid"
              borderColor="inquiryOverview.softBorder"
              boxShadow="sm"
              mt={4}
            >
              <CardBody p={{ base: 4, md: 5 }}>
                {isMatrixMaster ? (
                  <InquiryTable
                    masterInquiry={inquiry}
                    inquiries={displayedInquiries}
                    missingCellSlots={missingCellSlots}
                    allTierColumns={matrixTierColumns}
                    targetTierQuantities={matrixTargetTierQuantities}
                    hasAnyX={hasAnyX}
                    hasAnyY={hasAnyY}
                    hideIdentityColumns={true}
                    toolbarControls={
                      matrixCellFilters.hasCellFilters ? (
                        <>
                          {matrixCellFilters.cellXOptions.length > 0 && (
                            <Box>
                              <TexasSelect<ReactSelectOption>
                                isClearable={true}
                                aria-label={
                                  inquiry.matrixNodeXName ?? t('general.cellX')
                                }
                                value={matrixCellFilters.cellX}
                                onChange={(selected) =>
                                  matrixCellFilters.setCellX(
                                    selected as ReactSelectOption | null,
                                  )
                                }
                                options={matrixCellFilters.cellXOptions}
                                placeholder={
                                  inquiry.matrixNodeXName ?? t('general.cellX')
                                }
                              />
                            </Box>
                          )}
                          {matrixCellFilters.cellYOptions.length > 0 && (
                            <Box>
                              <TexasSelect<ReactSelectOption>
                                isClearable={true}
                                aria-label={
                                  inquiry.matrixNodeYName ?? t('general.cellY')
                                }
                                value={matrixCellFilters.cellY}
                                onChange={(selected) =>
                                  matrixCellFilters.setCellY(
                                    selected as ReactSelectOption | null,
                                  )
                                }
                                options={matrixCellFilters.cellYOptions}
                                placeholder={
                                  inquiry.matrixNodeYName ?? t('general.cellY')
                                }
                              />
                            </Box>
                          )}
                        </>
                      ) : null
                    }
                  />
                ) : (
                  <NonMatrixInquiryTierPrices
                    inquiry={inquiry}
                    currencyCode={currencyCode}
                  />
                )}
              </CardBody>
            </Card>
          </Box>
          <Box minW={0}>
            <InquiryChargesReadOnly
              inquiry={inquiry}
              isAccepted={inquiry.acceptedDate != null}
              onEdit={onOpen}
            />
            <InquiryChargesDrawer
              isOpen={isOpen}
              onClose={onClose}
              inquiry={inquiry}
            />
          </Box>
        </Grid>
        <VerifyDialogWithRequest
          headerTitle={t('inquiry.confirmCopyHeader')}
          secondaryButtonTitle={t('general.cancel')}
          primaryButtonTitle={t('inquiry.copy')}
          primaryRequest={inquiryApi.copyInquiry}
          args={[inquiry.id]}
          isOpen={isCopyOpen}
          onClose={onCopyClose}
          onPerformed={(newInquiry) => {
            reactEvents.routingChanged.dispatch({
              to: 'variant.inquiry.detail',
              navObj: {
                compositionGroupId: newInquiry.compositionGroupPurchaseInfoId,
                inquiryId: newInquiry.id,
              },
            });
          }}
          onSuccessTitle={t('inquiry.copied')}
        >
          {t('inquiry.confirmCopy')}
        </VerifyDialogWithRequest>
        <InquiryStatusDialog
          action="accept"
          inquiry={inquiry}
          isOpen={isAcceptOpen}
          onClose={onAcceptClose}
          onPerformed={() => {
            refetch();
            refetchPage({ variantId });
          }}
          primaryRequest={inquiryApi.acceptInquiry}
        />
        <InquiryStatusDialog
          action="reject"
          inquiry={inquiry}
          isOpen={isRejectOpen}
          onClose={onRejectClose}
          onPerformed={() => {
            refetch();
            refetchPage({ variantId });
          }}
          primaryRequest={inquiryApi.rejectInquiry}
        />
      </Box>
    </BodyOverride>
  );
}
